import {
  Drawer,
  Group,
  Select,
  Stack,
  Text,
  Textarea,
  Title,
} from "@mantine/core"
import {
  CancelButton,
  DeleteButtonWithConfirmModal,
  SubmitButton,
} from "@components/Buttons"
import { Form } from "@components/typed-form"
import { SuspenseWithErrorBoundary } from "@components/suspense"
import { showErrorNotification } from "@/utils/form-submission-notifications"
import { useTranslation } from "react-i18next"
import { DefaultError } from "@components/error-context"
import {
  hasValidationErrors,
  tryGetValidationErrorsAsMantineErrors,
} from "@/utils/to-mantine-validation-errors"
import { DatePickerInput, TimeInput } from "@mantine/dates"
import { mapNullable } from "@/utils/nullable"
import { PersonStatusDto } from "@/@client/Theasoft_ApiServer_Orchestra"
import { usePostMutation } from "@/typed-client/hooks/use-post-mutation"
import { useSuspenseQueryGet } from "@/typed-client/hooks/use-suspense-query-get"
import { RenderSimplePerson } from "@/blocks/persons-and-users/User"
import { dateToDateOnly } from "@/utils/date-to-date-only"
import { RenderShiftType } from "@/blocks/shift-types/render-shift-type"

export function EditAbsenceModal(props: {
  personStatus: PersonStatusDto | null
  onEdited: () => void
  onCancel: () => void
  departmentId: number
}) {
  const { t } = useTranslation("app_server")
  return (
    <Drawer
      position="right"
      size="lg"
      overlayProps={{ backgroundOpacity: 0.2, blur: 0 }}
      opened={props.personStatus !== null}
      onClose={() => props.onCancel()}
      title={<Title order={3}>{t("EditAbsence")}</Title>}
    >
      <SuspenseWithErrorBoundary>
        {props.personStatus !== null ? (
          <EditAbsenceContent
            departmentId={props.departmentId}
            personStatus={props.personStatus}
            onEdited={props.onEdited}
            onCancel={props.onCancel}
          />
        ) : null}
      </SuspenseWithErrorBoundary>
    </Drawer>
  )
}

export function EditAbsenceContent({
  personStatus,
  onEdited,
  onCancel,
  departmentId,
}: {
  departmentId: number
  personStatus: PersonStatusDto
  onEdited: () => void
  onCancel: () => void
}) {
  const { t } = useTranslation("app_server")
  const deleteAbsence = usePostMutation("/api/Orchestra/DeleteAbsence")
  const { data: availableAbsenceShiftTypes } = useSuspenseQueryGet(
    "/api/ShiftType/GetRequestableAbsenceShiftTypesForDepartment",
    { departmentId }
  )

  return (
    <Form
      url="/api/Orchestra/ModifyAbsence"
      onSuccess={() => onEdited()}
      onError={(e, _, __, f) => {
        if (e instanceof DefaultError && hasValidationErrors(e)) {
          const validationErrors = tryGetValidationErrorsAsMantineErrors(e, t)
          if (validationErrors) {
            f.setErrors(validationErrors)
          }
        }
        showErrorNotification(e)
      }}
      initialValues={{
        dateRange: [
          new Date(personStatus.startDate),
          new Date(personStatus.endDate),
        ],
        shiftTypeId: personStatus.shiftTypeId.toString(),
        startTime: personStatus.startTime,
        endTime: personStatus.endTime,
        note: personStatus.note,
      }}
      transform={(v) => {
        const startDate = mapNullable(v.dateRange[0], dateToDateOnly)
        const endDate = mapNullable(v.dateRange[1], dateToDateOnly)
        return {
          departmentId,
          personStatusId: personStatus.personStatusId,
          shiftTypeId: parseInt(v.shiftTypeId),
          startDate: startDate!,
          startTime: v.startTime,
          endDate: endDate!,
          endTime: v.endTime,
          note: v.note,
        }
      }}
    >
      {(f, _, m) => {
        const shiftTypeId = f.values.shiftTypeId
          ? parseInt(f.values.shiftTypeId)
          : null
        const isRequestableShiftType =
          shiftTypeId !== null &&
          availableAbsenceShiftTypes.some((v) => v.shiftTypeId === shiftTypeId)
        const readonly = !isRequestableShiftType || !personStatus.isEditable

        const errors = Object.keys(f.errors).map((k) => f.errors[k])
        return (
          <Stack>
            <Text fw={700}>
              {personStatus.personId ? (
                <RenderSimplePerson suspense personId={personStatus.personId} />
              ) : personStatus.contractId ? (
                <RenderSimplePerson
                  suspense
                  contractId={personStatus.contractId}
                />
              ) : (
                "N/A"
              )}
            </Text>
            <DatePickerInput
              readOnly={readonly}
              type="range"
              label={t("TimePeriod")}
              placeholder={t("TimePeriod")}
              {...f.getInputProps(_.dateRange._PATH_)}
            />
            <Group>
              <TimeInput
                readOnly={readonly}
                label={t("StartTime")}
                {...f.getInputProps(_.startTime._PATH_)}
              />
              <TimeInput
                readOnly={readonly}
                label={t("EndTime")}
                {...f.getInputProps(_.endTime._PATH_)}
              />
            </Group>
            {isRequestableShiftType ? (
              <Select
                readOnly={readonly}
                {...f.getInputProps(_.shiftTypeId._PATH_)}
                label={t("ReasonForAbsence")}
                data={availableAbsenceShiftTypes.map((v) => ({
                  label: v.name,
                  value: v.shiftTypeId.toString(),
                }))}
              />
            ) : shiftTypeId ? (
              <RenderShiftType id={shiftTypeId}>
                {(v) => v.name}
              </RenderShiftType>
            ) : null}
            <Textarea
              readOnly={readonly}
              label={t("Note")}
              {...f.getInputProps(_.note._PATH_)}
            />
            <Group justify="space-between" mt="md">
              <CancelButton onClick={onCancel} />

              <DeleteButtonWithConfirmModal
                disabled={!isRequestableShiftType}
                confirmModalProps={{
                  confirmProps: { loading: deleteAbsence.isPending },
                  onConfirm: () =>
                    deleteAbsence.mutate(
                      {
                        personStatusId: personStatus.personStatusId,
                      },
                      {
                        onSuccess: () => {
                          onEdited()
                        },
                      }
                    ),
                }}
              />

              <SubmitButton
                disabled={!isRequestableShiftType || errors.length > 0}
                loading={m.isPending}
              >
                {t("EditAbsence")}
              </SubmitButton>
            </Group>
          </Stack>
        )
      }}
    </Form>
  )
}
